import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  useConversations,
  useConversation,
  useConversationMessages,
  useCreateConversation,
  useDeleteConversation,
  useSendMessage,
  useGenerateChatReply,
} from '../hooks/useChat'
import { updateConversation, getTaskDraft, ApiError } from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { useAppStore } from '../stores/app'
import { useContentTasks, useContentTasksByConversation } from '../hooks/useContentTasks'
import { ChatSidebar } from '../components/chat/ChatSidebar'
import { ChatMessageList } from '../components/chat/ChatMessageList'
import { ChatInput } from '../components/chat/ChatInput'
import { ChatSourceControls } from '../components/chat/ChatSourceControls'
import { TaskDraftReviewModal } from '../components/content/TaskDraftReviewModal'
import type { TaskDraftResponse } from '../lib/api'
import type { ContentTaskCreateRequest } from '../lib/api'
import { Pencil, Check, X, FileText, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

export function ChatPage() {
  const navigate = useNavigate()
  const { conversationId: routeConversationId } = useParams<{
    conversationId?: string
  }>()
  const queryClient = useQueryClient()
  const organizationId = useAppStore((state) => state.organizationId)
  const [inputValue, setInputValue] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleEditValue, setTitleEditValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [draftFromConversation, setDraftFromConversation] = useState<TaskDraftResponse | null>(null)
  const [loadingDraft, setLoadingDraft] = useState(false)

  const conversationId = routeConversationId ?? null

  const { createTask, isCreating: isCreatingTask } = useContentTasks()
  const { data: linkedTasks = [] } = useContentTasksByConversation(conversationId)
  const generateReply = useGenerateChatReply()

  const { data: conversations = [], isLoading: conversationsLoading } =
    useConversations(50, 0)
  const createConv = useCreateConversation()
  const deleteConv = useDeleteConversation()
  const sendMsg = useSendMessage()
  const { data: conversation, isLoading: conversationLoading } =
    useConversation(conversationId)
  const { data: messages = [], isLoading: messagesLoading } =
    useConversationMessages(conversationId)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages.length, scrollToBottom])

  const handleNewConversation = useCallback(() => {
    createConv.mutate(undefined, {
      onSuccess: (data) => {
        navigate(`/chat/${data.id}`, { replace: true })
      },
    })
  }, [createConv, navigate])

  const handleSelectConversation = useCallback(
    (id: string) => {
      navigate(`/chat/${id}`)
    },
    [navigate]
  )

  const handleDeleteConversation = useCallback(
    (id: string) => {
      deleteConv.mutate(id, {
        onSuccess: () => {
          if (routeConversationId === id) {
            navigate('/chat', { replace: true })
          }
        },
      })
    },
    [deleteConv, navigate, routeConversationId]
  )

  const handleSendMessage = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed || !conversationId) return
    sendMsg.mutate(
      { conversationId, role: 'user', content: trimmed },
      {
        onSuccess: () => {
          setInputValue('')
          generateReply.mutate(conversationId)
        },
      }
    )
  }, [inputValue, conversationId, sendMsg, generateReply])

  const startEditingTitle = useCallback(() => {
    setTitleEditValue(conversation?.title ?? '')
    setEditingTitle(true)
  }, [conversation?.title])

  const saveTitle = useCallback(async () => {
    if (!conversationId) return
    await updateConversation(conversationId, {
      title: titleEditValue.trim() || undefined,
    })
    setEditingTitle(false)
    queryClient.invalidateQueries({ queryKey: ['chat', 'conversation', conversationId] })
    queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] })
  }, [conversationId, titleEditValue, queryClient])

  const cancelEditingTitle = useCallback(() => {
    setTitleEditValue('')
    setEditingTitle(false)
  }, [])

  const handleStartProject = useCallback(async () => {
    if (!conversationId || !organizationId) return
    setLoadingDraft(true)
    try {
      const draft = await getTaskDraft(conversationId)
      setDraftFromConversation(draft)
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error('Error al obtener borrador', { description: err.message })
      } else {
        toast.error('Error al obtener borrador', { description: getErrorMessage(err) })
      }
    } finally {
      setLoadingDraft(false)
    }
  }, [conversationId, organizationId])

  const handleCreateTaskFromDraft = useCallback(
    async (payload: ContentTaskCreateRequest) => {
      const data = conversationId ? { ...payload, conversation_id: conversationId } : payload
      await createTask(data)
      setDraftFromConversation(null)
      navigate('/content')
    },
    [conversationId, createTask, navigate]
  )

  const displayTitle = conversation?.title ?? 'Sin título'
  const isSending = sendMsg.isPending || generateReply.isPending

  return (
    <div className="flex h-[calc(100vh-0px)] bg-slate-50">
      <ChatSidebar
        conversations={conversations}
        isLoading={conversationsLoading}
        selectedId={conversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
      />
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        {!conversationId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-8">
            <p className="text-lg font-medium">Selecciona una conversación</p>
            <p className="text-sm mt-1">
              o crea una nueva para empezar a chatear.
            </p>
            <button
              type="button"
              onClick={handleNewConversation}
              disabled={createConv.isPending}
              className="mt-6 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              Nueva conversación
            </button>
          </div>
        ) : (
          <>
            <header className="flex-shrink-0 h-14 border-b border-slate-200 px-4 flex items-center gap-2">
              {editingTitle ? (
                <>
                  <input
                    type="text"
                    value={titleEditValue}
                    onChange={(e) => setTitleEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTitle()
                      if (e.key === 'Escape') cancelEditingTitle()
                    }}
                    className="flex-1 min-w-0 px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    autoFocus
                    aria-label="Editar título"
                  />
                  <button
                    type="button"
                    onClick={saveTitle}
                    className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50"
                    aria-label="Guardar título"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditingTitle}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100"
                    aria-label="Cancelar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <h1 className="flex-1 min-w-0 truncate text-lg font-semibold text-slate-800">
                    {conversationLoading ? '...' : displayTitle}
                  </h1>
                  <button
                    type="button"
                    onClick={handleStartProject}
                    disabled={loadingDraft || !organizationId}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    title="Iniciar proyecto desde esta conversación"
                  >
                    <FileText className="w-4 h-4" />
                    {loadingDraft ? 'Cargando...' : 'Iniciar proyecto'}
                  </button>
                  <button
                    type="button"
                    onClick={startEditingTitle}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Editar título"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </>
              )}
            </header>
            {linkedTasks.length > 0 && (
              <div className="flex-shrink-0 border-b border-slate-200 px-4 py-2 bg-slate-50/80">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Tareas vinculadas
                </p>
                <ul className="flex flex-wrap gap-2">
                  {linkedTasks.map((t) => (
                    <li key={t.id}>
                      <Link
                        to={`/content/${t.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm bg-white border border-slate-200 text-slate-700 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 transition-colors"
                      >
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate max-w-[180px]">{t.topic}</span>
                        <span className="text-xs text-slate-400">
                          {t.content_type} · {t.channel}
                        </span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0 text-slate-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex-shrink-0 border-b border-slate-200 px-4 py-3 bg-white">
              <ChatSourceControls
                conversationId={conversationId}
                activeSources={conversation?.active_sources ?? undefined}
              />
            </div>
            <ChatMessageList
              messages={messages}
              isLoading={messagesLoading || isSending}
              className="flex-1 min-h-0"
            />
            <div ref={messagesEndRef} />
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSendMessage}
              disabled={isSending}
            />
          </>
        )}
      </div>
      <TaskDraftReviewModal
        isOpen={draftFromConversation !== null}
        onClose={() => setDraftFromConversation(null)}
        draft={draftFromConversation}
        onCreateTask={handleCreateTaskFromDraft}
        isLoading={isCreatingTask}
        conversationId={conversationId}
      />
    </div>
  )
}
